import { useState, useEffect, useCallback } from 'react';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';

/**
 * Hook for managing biometric authentication (fingerprint, face recognition, etc.)
 * Uses WebAuthn API for web and native Capacitor integration for mobile
 * Stores biometric preferences locally
 */
export function useBiometric() {
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricType, setBiometricType] = useState(null); // 'FINGERPRINT', 'FACE', 'WEBAUTHN'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const BIOMETRIC_PREFS_KEY = 'biometric_auth_enabled';
  const BIOMETRIC_EMAIL_KEY = 'biometric_email';
  const BIOMETRIC_TYPE_KEY = 'biometric_device_type';
  const BIOMETRIC_PASSWORD_KEY = 'biometric_password';
  const BIOMETRIC_CREDENTIAL_ID_KEY = 'biometric_credential_id';

  const arrayBufferToBase64Url = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i += 1) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  };

  const base64UrlToUint8Array = (value = '') => {
    const normalized = String(value || '').replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '==='.slice((normalized.length + 3) % 4);
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  };

  // Check if device supports biometric authentication
  const checkBiometricAvailability = useCallback(async () => {
    try {
      setLoading(true);
      const isNativeAndroid = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
      
      // Check for WebAuthn support (works in modern browsers including on Android)
      const webAuthnAvailable = window.PublicKeyCredential !== undefined && 
                                typeof window.PublicKeyCredential === 'function';
      
      let hasWebAuthn = false;
      let biometryType = 'WEBAUTHN';

      if (webAuthnAvailable) {
        try {
          if (typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function') {
            hasWebAuthn = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          } else if (isNativeAndroid) {
            // Some Android WebView builds do not expose this capability probe reliably.
            hasWebAuthn = true;
          }
        } catch (e) {
          console.warn('WebAuthn availability check failed:', e);
          if (isNativeAndroid) {
            hasWebAuthn = true;
          }
        }
      }

      // Try to detect if we're on Android with native biometric support
      if (isNativeAndroid || /Android/.test(navigator.userAgent)) {
        biometryType = 'FINGERPRINT'; // Android typically has fingerprint capability
      }

      setBiometricAvailable(webAuthnAvailable && hasWebAuthn);
      
      if (webAuthnAvailable && hasWebAuthn) {
        setBiometricType(biometryType);
      }
      
      // Check if user has enabled biometric login
      const { value: enabled } = await Preferences.get({ key: BIOMETRIC_PREFS_KEY });
      setBiometricEnabled(enabled === 'true');
      
      setError(null);
    } catch (err) {
      console.error('Biometric availability check failed:', err);
      setBiometricAvailable(false);
      setBiometricEnabled(false);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a credential for biometric authentication
  const createBiometricCredential = useCallback(async (email) => {
    try {
      if (!navigator?.credentials?.create) {
        throw new Error('WebAuthn create() is niet beschikbaar op dit apparaat');
      }

      const challengeArray = new Uint8Array(32);
      crypto.getRandomValues(challengeArray);

      const rpId = window.location.hostname;

      const attestationResponse = await navigator.credentials
        .create({
          publicKey: {
            challenge: challengeArray,
            rp: {
              name: 'Krinkels Robot Beheer',
              id: rpId
            },
            user: {
              id: new TextEncoder().encode(email),
              name: email,
              displayName: email
            },
            pubKeyCredParams: [{ alg: -7, type: 'public-key' }],
            authenticatorSelection: {
              authenticatorAttachment: 'platform',
              userVerification: 'required'
            },
            timeout: 60000,
            attestation: 'direct'
          }
        });

      return attestationResponse;
    } catch (err) {
      console.error('Error creating biometric credential:', err);
      throw err;
    }
  }, []);

  // Enable biometric authentication for current user
  const enableBiometric = useCallback(async (userEmail) => {
    try {
      setLoading(true);
      setError(null);

      if (!biometricAvailable) {
        setError('Biometrische authenticatie niet beschikbaar op dit apparaat');
        return false;
      }

      // Create a biometric credential
      const credential = await createBiometricCredential(userEmail);

      if (!credential) {
        setError('Kon biometrische inloggegevens niet opstellen');
        return false;
      }

      // Store biometric metadata
      await Preferences.set({ key: BIOMETRIC_EMAIL_KEY, value: userEmail });
      await Preferences.set({ key: BIOMETRIC_PREFS_KEY, value: 'true' });
      await Preferences.remove({ key: BIOMETRIC_PASSWORD_KEY });
      
      if (biometricType) {
        await Preferences.set({ key: BIOMETRIC_TYPE_KEY, value: biometricType });
      }

      if (credential?.rawId) {
        await Preferences.set({ key: BIOMETRIC_CREDENTIAL_ID_KEY, value: arrayBufferToBase64Url(credential.rawId) });
      }

      setBiometricEnabled(true);
      return true;
    } catch (err) {
      console.error('Error enabling biometric:', err);
      setError(err.message || 'Kon biometrische inloggen niet inschakelen');
      return false;
    } finally {
      setLoading(false);
    }
  }, [biometricAvailable, biometricType, createBiometricCredential]);

  // Disable biometric authentication
  const disableBiometric = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get stored email
      const { value: email } = await Preferences.get({ key: BIOMETRIC_EMAIL_KEY });

      if (!email) {
        setError('Geen biometrische inloggegevens gevonden');
        return false;
      }

      // Remove stored biometric credentials
      await Preferences.remove({ key: BIOMETRIC_EMAIL_KEY });
      await Preferences.remove({ key: BIOMETRIC_PREFS_KEY });
      await Preferences.remove({ key: BIOMETRIC_TYPE_KEY });
      await Preferences.remove({ key: BIOMETRIC_PASSWORD_KEY });
      await Preferences.remove({ key: BIOMETRIC_CREDENTIAL_ID_KEY });

      setBiometricEnabled(false);
      setError(null);
      return true;
    } catch (err) {
      console.error('Error disabling biometric:', err);
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Authenticate user with biometric
  const authenticateWithBiometric = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!biometricAvailable) {
        setError('Biometrische authenticatie niet beschikbaar op dit apparaat');
        return null;
      }

      if (!navigator?.credentials?.get) {
        setError('WebAuthn get() is niet beschikbaar op dit apparaat/browser');
        return null;
      }

      // Retrieve stored email
      const { value: email } = await Preferences.get({ key: BIOMETRIC_EMAIL_KEY });
      const { value: storedPassword } = await Preferences.get({ key: BIOMETRIC_PASSWORD_KEY });
      const { value: credentialId } = await Preferences.get({ key: BIOMETRIC_CREDENTIAL_ID_KEY });

      if (!email) {
        setError('Geen opgeslagen biometrische inloggegevens gevonden');
        return null;
      }

      const challengeArray = new Uint8Array(32);
      crypto.getRandomValues(challengeArray);

      // Try to get the credential
      const publicKey = {
        challenge: challengeArray,
        timeout: 60000,
        userVerification: 'required',
        rpId: window.location.hostname,
      };

      if (credentialId) {
        try {
          publicKey.allowCredentials = [{
            id: base64UrlToUint8Array(credentialId),
            type: 'public-key',
            transports: ['internal', 'hybrid'],
          }];
        } catch (decodeError) {
          console.warn('Could not decode stored credential id, falling back to discoverable get():', decodeError);
        }
      }

      const assertion = await navigator.credentials.get({ publicKey });

      if (!assertion) {
        setError('Biometrische authenticatie geannuleerd');
        return null;
      }

      // Automatic login can only happen if a password was stored after a successful normal login.
      if (!storedPassword) {
        return { email };
      }

      return { email, password: storedPassword };
    } catch (err) {
      console.error('Biometric authentication error:', err);
      setError(err.message || 'Biometrische authenticatie mislukt');
      return null;
    } finally {
      setLoading(false);
    }
  }, [biometricAvailable]);

  // Get biometric device type display name
  const getBiometricTypeName = useCallback(() => {
    switch (biometricType) {
      case 'FINGERPRINT':
        return 'Vingerafdruk';
      case 'FACE':
        return 'Gezichtsherkenning';
      case 'IRIS':
        return 'Iris-scan';
      case 'WEBAUTHN':
        return 'Biometrische verificatie';
      default:
        return 'Biometrisch';
    }
  }, [biometricType]);

  // Check availability on mount
  useEffect(() => {
    checkBiometricAvailability();
  }, [checkBiometricAvailability]);

  return {
    biometricAvailable,
    biometricEnabled,
    biometricType,
    loading,
    error,
    enableBiometric,
    disableBiometric,
    authenticateWithBiometric,
    BIOMETRIC_PREFS_KEY,
    BIOMETRIC_EMAIL_KEY,
    BIOMETRIC_PASSWORD_KEY,
    getBiometricTypeName,
    checkBiometricAvailability
  };
}
